import { createSlice } from '@reduxjs/toolkit';
import { theme } from '../theme/theme';

/**
 * Redux slice for global app info state
 * Manages UI state (loader, toasts, alerts), app settings, cafes, payment methods, and dynamic theme
 */
const InfoSlice = createSlice({
  name: 'info',
  initialState: {
    isTablet: null,
    info: {},
    cafes: [],
    status: 'idle',
    error: null,
    show_toast: false,
    toast_msg: null,
    loader: false,
    show_alert: false,
    alert_msg: null,
    alert_title: 'Alert',
    statusBarStyle: 'light-content',
    statusBarColor: theme.colors.primary,
    cafe: null,
    isNetworkConnected: true,
    review_order: null,
    visibleOrderReviewModal: false,
    reviewOptions: [],
    payment_gateways: [],
    cc_payment_methods: [],
    otpInfo: null,
    cafeCode: null,
  },
  reducers: {
    /** Sets app info (theme, settings, etc.) */
    setInfo(state, action) {
      state.status = 'succeeded';
      state.info = action.payload;
    },
    /** Sets whether device is a tablet */
    setIsTablet(state, action) {
      state.isTablet = action.payload;
    },
    /** Clears payment gateways and cash counter payment methods */
    resetPaymentGateways(state, action) {
      state.payment_gateways = [];
      state.cc_payment_methods = [];
    },
    /** Sets available payment gateways */
    setPaymentGateways(state, action) {
      state.payment_gateways = action.payload;
    },
    /** Sets cash counter payment methods */
    setCCPaymentMethods(state, action) {
      state.cc_payment_methods = action.payload;
    },
    /** Sets list of cafes/restaurants */
    setCafes(state, action) {
      state.cafes = action.payload;
    },
    /** Sets cafe code for signup */
    setCafeCode(state, action) {
      state.cafeCode = action.payload;
    },
    /** Sets OTP info (mobile/email, action) */
    setOTPInfo(state, action) {
      state.otpInfo = action.payload;
    },
    /** Sets review options for order feedback */
    setReviewOptions(state, action) {
      state.reviewOptions = action.payload;
    },
    /** Sets order to review */
    setReviewOrder(state, action) {
      state.review_order = action.payload;
    },
    /** Shows order review modal with order data */
    showOrderReviewModal(state, action) {
      state.review_order = action.payload;
      state.visibleOrderReviewModal = true;
    },
    /** Hides order review modal */
    hideOrderReviewModal(state, action) {
      state.review_order = null;
      state.visibleOrderReviewModal = false;
    },
    /** Shows toast notification */
    showToast(state, action) {
      state.toast_msg = action.payload;
      state.show_toast = true;
    },
    /** Sets network connection status */
    setNetworkConnected(state, action) {
      state.isNetworkConnected = action.payload;
    },
    /** Hides toast notification */
    hideToast(state, action) {
      state.show_toast = false;
      state.toast_msg = null;
    },
    /** Shows global loader */
    showLoader(state, action) {
      state.loader = true;
    },
    /** Hides global loader */
    hideLoader(state, action) {
      state.loader = false;
    },
    /** Shows alert dialog with message */
    showAlert(state, action) {
      state.alert_msg = action.payload;
      state.show_alert = true;
    },
    /** Hides alert dialog */
    hideAlert(state, action) {
      state.show_alert = false;
      state.alert_msg = null;
      state.alert_title = 'Alert'
    },
    /** Sets custom alert title */
    setAlertTitle(state, action) {
      state.alert_title = action.payload;
    },
    /** Sets status bar style (light-content/dark-content) */
    setStatusBarStyle(state, action) {
      state.statusBarStyle = action.payload;
    },
    setStatusBarColor(state, action) {
      state.statusBarColor = action.payload;
    },
    setSignupCafe(state, action) {
      state.cafe = action.payload;
    },
  },
});

export const {
  setDarkStatusbar,
  setLightStatusbar,
  showToast,
  hideToast,
  showLoader,
  hideLoader,
  showAlert,
  hideAlert,
  setStatusBarStyle,
  setSignupCafe,
  setNetworkConnected,
  setStatusBarColor,
  setAlertTitle,
  setInfo,
  setReviewOrder,
  showOrderReviewModal,
  hideOrderReviewModal,
  setReviewOptions,
  setOTPInfo,
  setCafeCode,
  setCafes,
  setIsTablet,
  setPaymentGateways,
  setCCPaymentMethods,
  resetPaymentGateways,
} = InfoSlice.actions;
export default InfoSlice.reducer;
